import { useState, useEffect, useCallback, useRef } from 'react';
import { ApiFactory } from '../../core/config/ApiFactory';
import { IItemPedidoKDS, StatusItemKDS } from '../interfaces/IKDS';

const STATUSES_INATIVOS = [StatusItemKDS.CANCELADO, StatusItemKDS.LIBERADO];
const POLL_INTERVAL_MS = 10000;

export interface UseKDSPorPedidoReturn {
    loading: boolean;
    loadingAcao: boolean;
    erro: string | null;
    itens: IItemPedidoKDS[];
    itensPorEstacao: Map<string, IItemPedidoKDS[]>;
    temItensAtivos: boolean;
    refresh: () => void;
    iniciarItem: (uuid: string) => Promise<void>;
    concluirItem: (uuid: string) => Promise<void>;
    reverterItem: (uuid: string) => Promise<void>;
    concluirTodosItensPedido: (estacaoUuid: string) => Promise<void>;
    liberarPedido: () => Promise<void>;
}

export const useKDSPorPedido = (pedidoUuid: string | null): UseKDSPorPedidoReturn => {
    const [loading, setLoading] = useState(false);
    const [loadingAcao, setLoadingAcao] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [itens, setItens] = useState<IItemPedidoKDS[]>([]);

    const api = ApiFactory.getApi("MS_PEDIDOS");
    const abortRef = useRef<AbortController | null>(null);

    const carregar = useCallback(async (showLoading = false) => {
        if (!pedidoUuid) return;

        abortRef.current?.abort();
        abortRef.current = new AbortController();

        if (showLoading) setLoading(true);
        setErro(null);

        try {
            const res = await api.get(`/kds/pedido/${pedidoUuid}`, {
                signal: abortRef.current.signal,
            });
            setItens(res.data.itens ?? []);
        } catch (e: any) {
            if (e?.name === 'CanceledError' || e?.name === 'AbortError') return;
            setErro('Erro ao carregar itens KDS');
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [pedidoUuid]);

    useEffect(() => {
        if (!pedidoUuid) {
            setItens([]);
            setErro(null);
            return;
        }

        carregar(true);
        const interval = setInterval(() => carregar(false), POLL_INTERVAL_MS);

        return () => {
            clearInterval(interval);
            abortRef.current?.abort();
        };
    }, [carregar, pedidoUuid]);

    const itensPorEstacao = itens.reduce((map, item) => {
        const key = item.estacaoKdsUuid;
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(item);
        return map;
    }, new Map<string, IItemPedidoKDS[]>());

    const temItensAtivos = itens.some(i => !STATUSES_INATIVOS.includes(i.status));

    const comAcao = async (fn: () => Promise<void>) => {
        if (loadingAcao) return;
        setLoadingAcao(true);
        try {
            await fn();
            await carregar(false);
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingAcao(false);
        }
    };

    const iniciarItem = (uuid: string) =>
        comAcao(() => api.patch(`/kds/item/${uuid}/iniciar`));

    const concluirItem = (uuid: string) =>
        comAcao(() => api.patch(`/kds/item/${uuid}/concluir`));

    const reverterItem = (uuid: string) =>
        comAcao(() => api.patch(`/kds/item/${uuid}/reverter`));

    const concluirTodosItensPedido = (estacaoUuid: string) =>
        comAcao(() => api.patch(`/kds/estacao/${estacaoUuid}/pedido/${pedidoUuid}/concluir`));

    const liberarPedido = () =>
        comAcao(() => api.patch(`/kds/pedido/${pedidoUuid}/liberar`));

    return {
        loading,
        loadingAcao,
        erro,
        itens,
        itensPorEstacao,
        temItensAtivos,
        refresh: () => carregar(false),
        iniciarItem,
        concluirItem,
        reverterItem,
        concluirTodosItensPedido,
        liberarPedido,
    };
};
